import { useEffect, useRef, useState } from "react";
import { Music } from "lucide-react";
import { toast } from "sonner";

const VIDEO_ID = "XqUxKF_9gZs";

const loadYouTubeApi = () =>
  new Promise((resolve) => {
    if (window.YT?.Player) return resolve(window.YT);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve(window.YT);
    };
    if (!document.getElementById("yt-iframe-api")) {
      const s = document.createElement("script");
      s.id = "yt-iframe-api";
      s.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(s);
    }
  });

export default function VinylPlayer() {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi().then((YT) => {
      if (cancelled || !hostRef.current) return;
      playerRef.current = new YT.Player(hostRef.current, {
        videoId: VIDEO_ID,
        playerVars: { loop: 1, playlist: VIDEO_ID, playsinline: 1, controls: 0, rel: 0 },
        events: {
          onReady: () => setReady(true),
          onStateChange: (e) => setPlaying(e.data === YT.PlayerState.PLAYING || e.data === YT.PlayerState.BUFFERING),
          onError: () => toast.error("მუსიკის დაკვრა ვერ მოხერხდა, სცადეთ თავიდან"),
        },
      });
    });
    return () => {
      cancelled = true;
      playerRef.current?.destroy?.();
    };
  }, []);

  const toggle = () => {
    const player = playerRef.current;
    if (!ready || !player) {
      toast("მუსიკა იტვირთება, წამში სცადეთ თავიდან");
      return;
    }
    if (playing) {
      player.pauseVideo();
      setPlaying(false);
      return;
    }
    player.unMute?.();
    player.setVolume?.(100);
    player.playVideo();
    setPlaying(true);
  };

  return (
    <section data-testid="vinyl-player-container" className="space-y-5 text-center">
      <p className="eyebrow !text-[#B45309]">დააჭირე</p>

      <div className="turntable">
        <button
          data-testid="vinyl-player-toggle-button"
          onClick={toggle}
          className="mx-auto block rounded-full transition-transform hover:scale-[1.03] active:scale-95"
          aria-label={playing ? "მუსიკის შეჩერება" : "მუსიკის დაკვრა"}
        >
          <div
            data-testid="vinyl-spinning-disc"
            className={`vinyl-disc ${playing ? "spinning" : ""}`}
          >
            <div className="vinyl-label">
              <Music size={18} />
            </div>
          </div>
        </button>

        <div
          data-testid="vinyl-turntable-tonearm"
          className={`tonearm ${playing ? "playing" : ""}`}
          aria-hidden="true"
        >
          <span className="pivot" />
          <span className="arm" />
          <span className="head" />
        </div>
      </div>

      <div className="flex items-center justify-center gap-3">
        <div className={`eq ${playing ? "playing" : ""}`} aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>
        <p data-testid="vinyl-music-status-label" className="text-xs tracking-[0.18em] text-[#78716C]">
          {playing ? "ქართული ხმები — მრავალჟამიერ" : "საქორწინო მელოდია"}
        </p>
      </div>

      <div
        aria-hidden="true"
        className="pointer-events-none fixed bottom-0 left-0 h-px w-px overflow-hidden opacity-0"
      >
        <div ref={hostRef} />
      </div>
    </section>
  );
}
